import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Startseite from './pages/Startseite';
import AllInX from './pages/AllInX';
import MiningRace from './pages/MiningRace';
import MiBit from './pages/MiBit';
import Mitgliederbereich from './pages/Mitgliederbereich';
import Login from './pages/Login';
import Register from './pages/Register';
import PasswordReset from './pages/PasswordReset';
import ApproveRegistration from './pages/ApproveRegistration';
import EmailVerification from './pages/EmailVerification';
import Impressum from './pages/Impressum';
import FloatingCTA from './components/FloatingCTA';
import InstallBanner from './components/InstallBanner';
import UpdateBanner from './components/UpdateBanner';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Layout with Header and Footer (for public pages)
function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="main-content">{children}</main>
      <FloatingCTA />
      <InstallBanner />
      <Footer />
    </>
  );
}

// Minimal layout (for auth pages - no header/footer)
function MinimalLayout({ children }) {
  return <>{children}</>;
}

// Member layout (with Footer only - member area has its own header)
function MemberLayout({ children }) {
  return (
    <>
      {children}
      <Footer />
    </>
  );
}

function App() {
  // Prevent Safari/iPad from navigating to a dropped file (replaces the whole page with the image)
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <UpdateBanner />
          <Routes>
            {/* Public pages with Header/Footer */}
            <Route path="/" element={<PublicLayout><Startseite /></PublicLayout>} />
            {/* Alte Seite "Wie lege ich los" ist jetzt Teil der Startseite - alte Links/Lesezeichen weiterleiten */}
            <Route path="/wie-lege-ich-los" element={<Navigate to={{ pathname: '/', hash: '#wie-lege-ich-los' }} replace />} />
            <Route path="/all-inx" element={<PublicLayout><AllInX /></PublicLayout>} />
            <Route path="/mining-race" element={<PublicLayout><MiningRace /></PublicLayout>} />
            <Route path="/mibit" element={<PublicLayout><MiBit /></PublicLayout>} />
            <Route path="/impressum" element={<PublicLayout><Impressum /></PublicLayout>} />

            {/* Auth pages - minimal layout */}
            <Route path="/login" element={<MinimalLayout><Login /></MinimalLayout>} />
            <Route path="/registrieren" element={<MinimalLayout><Register /></MinimalLayout>} />
            <Route path="/passwort-vergessen" element={<MinimalLayout><PasswordReset /></MinimalLayout>} />
            <Route path="/passwort-zuruecksetzen" element={<MinimalLayout><PasswordReset /></MinimalLayout>} />
            <Route path="/passwort-zurücksetzen" element={<MinimalLayout><PasswordReset /></MinimalLayout>} />
            <Route path="/approve-registration" element={<MinimalLayout><ApproveRegistration /></MinimalLayout>} />
            <Route path="/email-bestaetigen" element={<MinimalLayout><EmailVerification /></MinimalLayout>} />

            {/* Protected member area - with Footer */}
            <Route
              path="/mitgliederbereich"
              element={
                <MemberLayout>
                  <ProtectedRoute>
                    <Mitgliederbereich />
                  </ProtectedRoute>
                </MemberLayout>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
